/**
 * Lightweight head-meta helper — no react-helmet dependency.
 * Sets / restores <meta> tags in document.head for the duration a component
 * is mounted.  Used by marketing pages so each route has its own OG + Twitter
 * card previews (#2455).
 *
 * Usage (inside a functional component):
 *   usePageMeta({
 *     title: "Pricing · 2hives.ai",
 *     description: "...",
 *     ogTitle: "...",
 *     ogDescription: "...",
 *   });
 */
import { useEffect } from "react";

const SITE_NAME  = "2hives.ai";
const OG_IMAGE   = "https://2hives.ai/og-image.png";
const BASE_TITLE = "2hives.ai — Background AI agents, running 24/7 on your projects";

export interface PageMetaOptions {
  title?: string;
  description?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  canonical?: string;
}

function setMeta(name: string, content: string, attr: "name" | "property" = "name"): () => void {
  const existing = document.querySelector(`meta[${attr}="${name}"]`);
  const prev = existing?.getAttribute("content") ?? null;
  if (existing) {
    existing.setAttribute("content", content);
  } else {
    const el = document.createElement("meta");
    el.setAttribute(attr, name);
    el.setAttribute("content", content);
    document.head.appendChild(el);
  }
  return () => {
    const el = document.querySelector(`meta[${attr}="${name}"]`);
    if (!el) return;
    if (prev === null) {
      el.remove();
    } else {
      el.setAttribute("content", prev);
    }
  };
}

function setTitle(title: string): () => void {
  const prev = document.title;
  document.title = title;
  return () => { document.title = prev; };
}

function setCanonical(url: string): () => void {
  const existing = document.querySelector<HTMLLinkElement>("link[rel='canonical']");
  const prev = existing?.getAttribute("href") ?? null;
  if (existing) {
    existing.setAttribute("href", url);
  } else {
    const el = document.createElement("link");
    el.rel = "canonical";
    el.href = url;
    document.head.appendChild(el);
  }
  return () => {
    const el = document.querySelector<HTMLLinkElement>("link[rel='canonical']");
    if (!el) return;
    if (prev === null) {
      el.remove();
    } else {
      el.setAttribute("href", prev);
    }
  };
}

export function usePageMeta(opts: PageMetaOptions): void {
  useEffect(() => {
    const restorers: Array<() => void> = [];

    const title       = opts.title       ?? BASE_TITLE;
    const description = opts.description ?? "";
    const ogTitle     = opts.ogTitle       ?? title;
    const ogDesc      = opts.ogDescription ?? description;
    const ogImage     = opts.ogImage       ?? OG_IMAGE;
    const twTitle     = opts.twitterTitle       ?? ogTitle;
    const twDesc      = opts.twitterDescription ?? ogDesc;
    const twImage     = opts.twitterImage       ?? ogImage;

    restorers.push(setTitle(title));
    if (description) restorers.push(setMeta("description", description));

    // Open Graph
    restorers.push(setMeta("og:title",       ogTitle,    "property"));
    restorers.push(setMeta("og:description", ogDesc,     "property"));
    restorers.push(setMeta("og:image",       ogImage,    "property"));
    restorers.push(setMeta("og:site_name",   SITE_NAME,  "property"));
    restorers.push(setMeta("og:type",        "website",  "property"));

    // Twitter / X card
    restorers.push(setMeta("twitter:card",        "summary_large_image"));
    restorers.push(setMeta("twitter:title",       twTitle));
    restorers.push(setMeta("twitter:description", twDesc));
    restorers.push(setMeta("twitter:image",       twImage));

    if (opts.canonical) {
      restorers.push(setCanonical(opts.canonical));
    }

    return () => { restorers.forEach(r => r()); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
